import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { LinkSimple, WarningCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { BTN_BLACK, BTN_WHITE } from "@/components/pageSettings/AdminModal";

const why = (u) => {
  if (u.flag?.deleted_name) return `Their delivery "${u.flag.deleted_name}" was deleted`;
  return u.dead ? "Linked to a delivery that no longer exists" : "Not linked to any delivery";
};

function RelinkRow({ u, deliveries, onDone }) {
  const [dest, setDest] = useState(u.suggested || "");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/buyer-logins/${u.id}/relink`, { destination_id: dest });
      toast.success(`${u.email} relinked`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't relink");
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="flex flex-col lg:flex-row lg:items-center gap-2 px-3 py-2" data-testid={`buyer-login-relink-${u.id}`}>
      <span className="font-bold text-sm break-all lg:w-64">{u.email}</span>
      <span className="flex-1 text-xs text-zinc-600">{why(u)}, so their dashboard shows no leads, delivery or brand.</span>
      <select value={dest} onChange={(e) => setDest(e.target.value)} data-testid={`buyer-login-relink-select-${u.id}`}
              className="border border-black px-2 py-1 text-xs max-w-full lg:w-72">
        <option value="">Pick their delivery…</option>
        {deliveries.map((d) => (
          <option key={d.id} value={d.id}>{d.company} · {d.name} ({d.type || "delivery"} …{d.id.slice(-4)})</option>
        ))}
      </select>
      <button type="button" className={`${BTN_BLACK} self-start lg:self-auto`} disabled={!dest || busy} onClick={go}
              data-testid={`buyer-login-relink-btn-${u.id}`}>
        <LinkSimple size={11} weight="bold" /> {busy ? "Relinking…" : "Relink"}
      </button>
    </li>
  );
}

function MovedRow({ u, onDone }) {
  const ok = () => api.post(`/admin/buyer-logins/${u.id}/dismiss`).then(onDone).catch(() => toast.error("Couldn't update"));
  return (
    <li className="flex flex-col sm:flex-row sm:items-center gap-2 px-3 py-2" data-testid={`buyer-login-moved-${u.id}`}>
      <span className="font-bold text-sm break-all sm:w-64">{u.email}</span>
      <span className="flex-1 text-xs text-zinc-600">
        Their delivery "{u.flag.deleted_name}" was deleted, so they were moved to {u.flag.moved_to?.join(", ") || "their other delivery"}.
      </span>
      <button type="button" className={`${BTN_WHITE} self-start sm:self-auto`} onClick={ok} data-testid={`buyer-login-moved-ok-${u.id}`}>OK</button>
    </li>
  );
}

// Clients page: buyer logins left without a delivery (relink them) or moved after a delivery was deleted.
export function BuyerLoginFixes() {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/buyer-logins/fixes").then((r) => setD(r.data)).catch(() => setD(null));
  useEffect(() => { load(); }, []);
  if (!d || (!d.relink.length && !d.moved.length)) return null;
  return (
    <section className="brut-card border-l-8 border-l-red-600 p-3 md:p-4 mb-4 space-y-2" data-testid="buyer-login-fixes">
      <div className="flex items-center gap-2 flex-wrap">
        <WarningCircle size={14} weight="fill" className="text-red-600" />
        <h2 className="font-chivo font-black uppercase tracking-tighter text-base md:text-lg">Buyer logins to fix</h2>
        <span className="label-mono text-zinc-600" data-testid="buyer-login-fixes-counts">
          {d.relink.length} need relinking · {d.moved.length} moved
        </span>
      </div>
      <ul className="border-2 border-black divide-y divide-zinc-200 bg-white">
        {d.relink.map((u) => <RelinkRow key={u.id} u={u} deliveries={d.deliveries} onDone={load} />)}
        {d.moved.map((u) => <MovedRow key={u.id} u={u} onDone={load} />)}
      </ul>
    </section>
  );
}
